<!-- eslint-disable vue/no-unused-properties -->
<script setup lang="ts">
import { computed, watch, useAttrs, useSlots, ref, type ComputedRef, nextTick, type Component } from 'vue';

import { ErrorMessage as VeeErrorMessage, TypedSchema, useField } from 'vee-validate';
import { InputType, BaseInputField } from '@/interfaces/InputFields';
import RuleType from '@/interfaces/RuleType';
import ConnxIcon from '@/components/Atoms/ConnxIcon.vue';
import { generateRules } from '@/lib/generate-rules';

import ConnxFieldDatePicker from './ConnxFieldDatePicker.vue';
import ConnxFieldSelect from './ConnxFieldSelect.vue';
import ConnxFieldChoice from './ConnxFieldChoice.vue';
import ConnxFieldDefault from './ConnxFieldDefault.vue';
import ConnxFieldFile from './ConnxFieldFile.vue';
import ConnxFieldTextarea from './ConnxFieldTextarea.vue';
import ConnxFieldButtonGroup from './ConnxFieldButtonGroup.vue';
import ConnxMultiSelect from './ConnxMultiSelect.vue';

const model = defineModel();

export interface ErrorUpdate {
  name: string;
  error: string | null;
}

const props = defineProps<
  BaseInputField & {
    autofocus?: boolean;
    disableImagePreview?: boolean;
    centered?: boolean;
    showPasswordToggle?: boolean;
    inline?: boolean;
    labelInline?: boolean;
    unpadded?: boolean;
    enableDatepickerTextInput?: boolean;
    seekResume?: File;
    labelPosition?: string;
    absoluteError?: boolean;
    format?: string;
    size?: 'small' | 'medium' | 'large';
    placeholder?: string;
    expanded?: boolean;
    minDate?: Date;
    maxDate?: Date;
  }
>();

const attrs = useAttrs();
const slots = useSlots();
const binds = computed(() => {
  const defaults = {
    type: props.type,
    value: props.value,
    label: props.label,
    autofocus: props.autofocus,
    disabled: props.disabled,
    name: props.name,
    required: props.required,
    maxlength: props.maxlength,
    placeholder: props.placeholder,
    inline: props.inline,
  };
  switch (props.type) {
    case InputType.Text:
      return {
        ...defaults,
      };
    case InputType.Checkbox:
    case InputType.ButtonGroup:
    case InputType.Radio:
    case InputType.Select:
    case InputType.MultiSelect:
      return {
        ...defaults,
        options: props.options,
        size: props.size,
        labelPosition: props.labelPosition,
      };
    case InputType.TextArea:
      return {
        ...defaults,
        expanded: props.expanded,
      };
    case InputType.File:
      return {
        ...defaults,
        mime: props.mime,
      };
    case InputType.Date:
      return {
        ...defaults,
        format: props.format,
        size: props.size,
        minDate: props.minDate,
        maxDate: props.maxDate,
        placeholder: props.placeholder,
        pickerType: props.pickerType,
      };
    default:
      return {
        ...defaults,
      };
  }
});
const imageUpload = props.type === InputType.File && props.mime === 'image/*';
const image = ref<string | undefined>('');
const fieldComponent = ref();

const defaultRules = (): TypedSchema => {
  if (!props.rules && (props.required || props.maxlength || props.type === InputType.File)) {
    let ruleType = RuleType.String;
    if (props.type === InputType.Checkbox) {
      ruleType = RuleType.Any;
    }
    if (props.type === InputType.Number) {
      ruleType = RuleType.Number;
    }
    if (props.type === InputType.URL) {
      ruleType = RuleType.URL;
    }
    if (props.type === InputType.File) {
      ruleType = RuleType.File;
    }
    if (props.maxlength) {
      return generateRules(ruleType, props.required, props.required ? 1 : 0, props.maxlength) as TypedSchema;
    }
    return generateRules(ruleType, props.required, 1) as TypedSchema;
  }

  // @ts-expect-error
  return props.rules!;
};

const inputType = ref(props.type);
const togglePassword = () => {
  if (inputType.value === InputType.Text) {
    inputType.value = InputType.Password;
  } else {
    inputType.value = InputType.Text;
  }
};

const { meta, errorMessage, validate, setTouched } = useField(props.name as string, defaultRules(), {
  initialValue: model,
  syncVModel: true,
  standalone: true,
});

const charactersLeft = computed(() => {
  if (props.maxlength) {
    return props.maxlength - String(model.value).length;
  }
  return 0;
});

const fieldMap = new Map<string, Component>([
  ['file', ConnxFieldFile],
  ['select', ConnxFieldSelect],
  ['multiselect', ConnxMultiSelect],
  ['radio', ConnxFieldChoice],
  ['checkbox', ConnxFieldChoice],
  ['button-group', ConnxFieldButtonGroup],
  ['date', ConnxFieldDatePicker],
  // ['date', ConnxFieldDatepicker],
  // ['time', ConnxFieldDatepicker],
  ['text', ConnxFieldDefault],
  ['textarea', ConnxFieldTextarea],
  ['search', ConnxFieldDefault],
  ['password', ConnxFieldDefault],
  ['number', ConnxFieldDefault],
  // ['date', ConnxFieldDatepicker],
  // ['datetime-local', ConnxFieldDatepicker],
  // ['date-range', ConnxFieldDatepicker],
  // ['time', ConnxFieldDatepicker],
  ['email', ConnxFieldDefault],
  ['hidden', ConnxFieldDefault],
  ['range', ConnxFieldDefault],
  ['tel', ConnxFieldDefault],
  ['url', ConnxFieldDefault],
]);

const mimeDisplayText = computed(() => {
  const array = props.mime?.split(',');

  return array
    ?.map((item) => {
      const str = item.replace(' ', '').replace('.', '');
      if (str === 'image/*') {
        return 'gif, jpg, png, jpeg, bmp';
      }

      return str;
    })
    .join(', ');
});

const imageLoaded = (val: string) => {
  if (val === '' && !model.value) {
    image.value = '';
  } else if (val) {
    image.value = val;
  }
};

const focus = () => {
  if (fieldComponent.value && typeof fieldComponent.value.focus === 'function') {
    fieldComponent.value.focus();
  }
};

watch([meta, errorMessage], ([newMeta, newError], [oldMeta, oldError]) => {
  if (newMeta !== oldMeta) {
    emit('update:meta', {
      name: props.name,
      meta: newMeta,
    });
  }
  if (newError !== oldError) {
    emit('update:error', {
      name: props.name,
      error: newError || null,
    } as ErrorUpdate);
  }
});

const emit = defineEmits(['update:meta', 'update:error']);
defineExpose({
  focus,
});
</script>

<template>
  <div
    :class="{
      'tw-mb-4': !attrs.class,
      'tw-flex tw-items-start tw-gap-x-4': labelInline,
    }"
  >
    <label
      v-if="label || slots.label"
      :for="name"
      class="Field__label block"
      :class="[
        `Field__label--${type}`,
        {
          'Field__label--inline tw-pt-2': labelInline,
        },
      ]"
    >
      <template v-if="label">
        {{ label }}
        <span v-if="required" class="tw-text-connx-danger">*</span>
        <small v-if="type === InputType.File" class="tw-block"> ({{ mimeDisplayText }}) 2MB limit </small>
      </template>
      <template v-else><slot name="label" /></template>
    </label>

    <div class="tw-relative">
      <div class="tw-relative">
        <!-- @vue-ignore -->
        <component
          :is="fieldMap.get(type)"
          ref="fieldComponent"
          v-bind="{
            ...binds,
            ...attrs,
          }"
          v-model="model"
          :type="inputType"
          class="tw-peer"
          :class="{
            'Field--unpadded': unpadded,
          }"
          :valid="meta.validated ? meta.valid : true"
          @image-loaded="(val) => imageLoaded(val)"
          @files-changed="(val) => (model = val)"
        >
          <template v-for="(_, name) in slots" #[name]="slotProps"
            ><slot :name="name" v-bind="slotProps"></slot
          ></template>
        </component>
        <div
          v-if="maxlength && charactersLeft >= 0 && !disabled && charactersLeft <= 10"
          class="tw-absolute tw-transform tw-hidden tw-z-10 peer-focus:tw-block tw-text-xs tw-right-0 Field__label Field__label--text tw-text-connx-white tw-bg-connx-primary tw-px-4 tw-py-1 tw-rounded"
          :class="{
            'peer-focus:tw-text-connx-danger !tw-text-connx-danger': charactersLeft < 0,
            'tw--bottom-1': errorMessage,
            'tw--bottom-8': errorMessage === undefined,
          }"
        >
          {{ String(model).length }}/{{ maxlength }}
        </div>
        <button
          v-if="showPasswordToggle"
          type="button"
          class="Field__password-toggle"
          :class="{
            'Field__password-toggle--active': inputType === InputType.Text,
          }"
          @click="togglePassword()"
        >
          <connx-icon name="fas fa-eye" class="tw-w-4 tw-h-4"></connx-icon>
        </button>
      </div>
      <img v-if="imageUpload && image && !disableImagePreview" :src="image" class="tw-mt-4 tw-w-12 tw-h-12" />
      <span
        v-if="errorMessage"
        class="Field__error tw-text-connx-danger tw-text-sm tw-w-full tw-font-semibold"
        :class="{
          'Field__error--absolute tw-z-1200': absoluteError,
        }"
      >
        {{ errorMessage }}
      </span>
    </div>
    <div v-if="notes" class="tw-mb-5">
      <p v-for="note in notes" :key="note" class="Field__label Field__label--note !tw-mb-0" v-text="note"></p>
    </div>
  </div>
</template>
